import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { Stack, TextField } from '@fluentui/react';
import { EditRegular, DeleteRegular, ArrowDownRegular, ArrowUpRegular, AddRegular } from '@fluentui/react-icons';
import { consultantBiosApi } from '../../api/api';
import styles from './styles.module.css';
import MasterList from './MasterList';
import { ConsultantBio } from '../../api';
import Button from '../../components/Button';
import EditConsultantBioModal from './components/EditConsultantBioModal';

const ConsultantBios: FC = () => {
  const consultantBiosMasterList = useRef<ConsultantBio[]>([]);
  const [consultantBios, setConsultantBios] = useState<ConsultantBio[]>([]);
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [showAddBio, setShowAddBio] = useState<boolean>(false);
  const [newConsultantBio, setNewConsultantBio] = useState<ConsultantBio | undefined>();

  const loadBios = useCallback(async () => {
    const bios = await consultantBiosApi.fetchAll();
    const biosSorted = bios.sort((a, b) => {
      if (sortOrder == 'desc') [b, a] = [a, b];
      return a.LastName.localeCompare(b.LastName);
    });
    consultantBiosMasterList.current = biosSorted;
    setConsultantBios(biosSorted);
  }, [sortOrder]);

  useEffect(() => {
    loadBios();
  }, [loadBios]);

  const deleteBio = useCallback(
    async (consultantBio: ConsultantBio) => {
      await consultantBiosApi.deleteOne({ consultantBio });
      loadBios();
    },
    [loadBios],
  );

  const editBio = useCallback((consultantBio: ConsultantBio) => {
    setNewConsultantBio(consultantBio);
    setShowAddBio(true);
  }, []);

  const filterBios = useCallback(
    (search: string) => {
      search = search.toLowerCase();
      const bios =
        search == ''
          ? consultantBiosMasterList.current
          : consultantBiosMasterList.current.filter(bio =>
              [bio.LastName, bio.FirstName, bio.Description].join(' ').toLowerCase().includes(search),
            );
      if (bios.length != consultantBios.length) setConsultantBios(bios);
    },
    [consultantBios.length],
  );

  const closeAddBio = useCallback(() => {
    setShowAddBio(false);
    setNewConsultantBio(undefined);
  }, []);

  const addBio = useCallback(
    async (consultantBio: ConsultantBio) => {
      await consultantBiosApi.upsertOne({ consultantBio });
      closeAddBio();
      loadBios();
    },
    [closeAddBio, loadBios],
  );

  return (
    <MasterList selectedTab="Consultant Biographies">
      <Stack horizontal style={{ justifyContent: 'space-between' }}>
        <Stack.Item>
          <TextField
            className={styles.textNarrow}
            type="text"
            placeholder="Search"
            onChange={(_, value) => filterBios(value ?? '')}
            onKeyUp={e => filterBios(e.currentTarget.value)}
          />
        </Stack.Item>
        <Stack.Item>
          <Stack horizontal style={{ gap: '16px' }}>
            <Button
              label="Sort"
              icon={sortOrder == 'asc' ? ArrowDownRegular : ArrowUpRegular}
              onClick={() => setSortOrder(sortOrder == 'asc' ? 'desc' : 'asc')}
            />
            <Button label="Add a New Consultant" icon={AddRegular} onClick={() => setShowAddBio(true)} />
          </Stack>
        </Stack.Item>
      </Stack>
      <Stack style={{ marginTop: '16px' }}>
        {consultantBios.map(consultantBio => (
          <Stack.Item key={`${consultantBio.PartitionKey}-${consultantBio.RowKey}`} className={styles.item}>
            <Stack horizontal style={{ gap: '16px' }}>
              <Stack.Item style={{ flexGrow: 1 }}>
                <div>
                  <strong>{`${consultantBio.LastName}, ${consultantBio.FirstName}`}</strong>
                </div>
                <div style={{ fontFamily: 'inherit', wordBreak: 'break-word', lineHeight: '1.5' }}>
                  {consultantBio.Description || ''}
                </div>
              </Stack.Item>
              <Button label="Edit" icon={EditRegular} onClick={() => editBio(consultantBio)} />
              <Button label="Delete" icon={DeleteRegular} onClick={() => deleteBio(consultantBio)} />
            </Stack>
          </Stack.Item>
        ))}
      </Stack>
      <EditConsultantBioModal
        isOpen={showAddBio}
        consultantBio={newConsultantBio}
        onSubmit={addBio}
        onCancel={closeAddBio}
      />
    </MasterList>
  );
};

export default ConsultantBios;
